import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, User, LogIn, ShieldCheck, Phone, MapPin, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { getUser } from "@/lib/userAuth";

const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/servizi", label: "Servizi", id: "servizi" },
  { to: "/prenota", label: "Prenota appuntamento", id: "prenota" },
  { to: "/news", label: "Newsletter CAF Labor", id: "newsletter" },
  { to: "/contatti", label: "Contatti", id: "contatti" },
];

const LOGO_URL = "https://customer-assets-cm19k8pv.emergentagent.net/job_cafe-workflow-chat/artifacts/riuxe5k0_FB_IMG_1784570505440.jpg";

const useAuthUser = () => {
  const [user, setUser] = useState(getUser());
  useEffect(() => {
    const sync = () => setUser(getUser());
    window.addEventListener("labor-auth-changed", sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("labor-auth-changed", sync); window.removeEventListener("storage", sync); };
  }, []);
  return user;
};

const LoginLinks = ({ user, close }) => (
  <div className="space-y-3">
    {user ? (
      <Link to="/profilo" onClick={close} data-testid="menu-profile"
        className="flex items-center gap-3 p-4 bg-labor-olive text-white font-semibold">
        <User className="w-5 h-5" /> <span className="flex-1">{user.first_name} · {user.labor_points ?? 0} punti</span> <ChevronRight className="w-4 h-4" />
      </Link>
    ) : (
      <Link to="/accedi" onClick={close} data-testid="menu-login-assistito"
        className="flex items-center gap-3 p-4 bg-labor-olive text-white font-bold underline underline-offset-4 decoration-2">
        <LogIn className="w-5 h-5" /> <span className="flex-1">Accedi Assistito ad Area Riservata</span> <ChevronRight className="w-4 h-4" />
      </Link>
    )}
    <Link to="/admin" onClick={close} data-testid="menu-login-operatore"
      className="flex items-center gap-3 p-4 bg-labor-ink text-white font-bold underline underline-offset-4 decoration-2">
      <ShieldCheck className="w-5 h-5" /> <span className="flex-1">Accedi Operatore Labor</span> <ChevronRight className="w-4 h-4" />
    </Link>
  </div>
);

export default function Navbar() {
  const loc = useLocation();
  const user = useAuthUser();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <header className="w-full border-b border-labor-border bg-labor-surface sticky top-0 z-40">
      <div className="tricolor-bar" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between py-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="CAF Labor Srl" className="h-12 md:h-14 w-auto object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-lg md:text-xl font-bold text-labor-ink">Labor AI</div>
            <div className="text-[10px] uppercase tracking-widest text-labor-muted">CAF · Patronato · Bitonto</div>
          </div>
        </Link>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="nav-menu-btn" aria-label="Apri menu"
              className="flex items-center gap-2 border border-labor-border px-4 py-2.5 hover:bg-labor-olive hover:text-white transition-colors font-semibold text-sm">
              <Menu className="w-5 h-5" /> <span className="hidden sm:inline">Menu</span>
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[88vw] sm:max-w-sm bg-labor-surface p-0 overflow-y-auto" data-testid="nav-menu-panel">
            <div className="tricolor-bar" />
            <div className="p-6">
              <SheetTitle className="font-display text-2xl text-labor-ink mb-6">Menu</SheetTitle>
              <nav className="flex flex-col mb-8">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} onClick={close} data-testid={`nav-link-${n.id}`}
                    className={`py-3.5 border-b border-labor-border text-lg font-medium flex items-center justify-between transition-colors ${loc.pathname === n.to ? "text-labor-terracotta" : "text-labor-ink hover:text-labor-olive"}`}>
                    {n.label} <ChevronRight className="w-4 h-4 opacity-40" />
                  </Link>
                ))}
              </nav>
              <LoginLinks user={user} close={close} />
              <div className="mt-8 text-sm text-labor-muted space-y-2">
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Via Zuavo n. 12 · Bitonto (BA)</div>
                <a href="tel:+393791466566" className="flex items-center gap-2 hover:text-labor-olive"><Phone className="w-4 h-4" /> 379 146 6566 · solo urgenze</a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
